import { Injectable } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { BehaviorSubject, fromEvent, Observable, filter } from 'rxjs';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: ReadonlyArray<string>;
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

@Injectable({
  providedIn: 'root'
})
export class PwaInstallService {
  private installPromptEvent: BeforeInstallPromptEvent | null = null;
  private isAppInstalled = false;
  
  // Observables para el estado de la PWA
  private installAvailableSubject = new BehaviorSubject<boolean>(false);
  private updateAvailableSubject = new BehaviorSubject<boolean>(false);
  private installReminderDismissed = false;
  private manualPromptShown = false;
  
  public installAvailable$ = this.installAvailableSubject.asObservable();
  public updateAvailable$ = this.updateAvailableSubject.asObservable();

  constructor(private swUpdate: SwUpdate) {
    this.initializeInstallPrompt();
    this.initializeUpdateCheck();
    this.checkIfAppInstalled();
    this.initializeProactiveInstallCheck();
  }

  private initializeInstallPrompt(): void {
    // Detectar cuando la app puede ser instalada
    fromEvent<BeforeInstallPromptEvent>(window, 'beforeinstallprompt')
      .subscribe(event => {
        event.preventDefault();
        this.installPromptEvent = event;
        
        // Solo mostrar si no ha sido rechazado anteriormente
        if (!this.installReminderDismissed && !this.isAppInstalled) {
          this.installAvailableSubject.next(true);
        }
      });

    // Detectar cuando la app ha sido instalada
    fromEvent(window, 'appinstalled').subscribe(() => {
      this.isAppInstalled = true;
      this.installAvailableSubject.next(false);
      this.saveInstallState('installed');
    });
  }

  private initializeUpdateCheck(): void {
    if (this.swUpdate.isEnabled) {
      // Verificar actualizaciones cada 30 minutos
      setInterval(() => {
        this.swUpdate.checkForUpdate();
      }, 30 * 60 * 1000);

      // Detectar cuando hay una actualización disponible
      this.swUpdate.versionUpdates
        .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
        .subscribe(() => {
          this.updateAvailableSubject.next(true);
        });
    }
  }

  private checkIfAppInstalled(): void {
    // Verificar si la app ya está instalada
    const installState = localStorage.getItem('pwa-install-state');
    if (installState === 'installed') {
      this.isAppInstalled = true;
    }

    // Verificar si el usuario ya rechazó la instalación
    const reminderState = localStorage.getItem('pwa-install-reminder');
    if (reminderState === 'dismissed') {
      this.installReminderDismissed = true;
    }

    // Detectar si se está ejecutando en modo standalone (instalada)
    if (window.matchMedia('(display-mode: standalone)').matches ||
        (window.navigator as any).standalone === true) {
      this.isAppInstalled = true;
      this.saveInstallState('installed');
    }
  }

  private initializeProactiveInstallCheck(): void {
    // Solo mostrar prompt manual si no está instalada y no ha sido rechazada
    if (!this.isAppInstalled && !this.installReminderDismissed) {
      // Esperar a que la página se cargue completamente
      setTimeout(() => {
        this.checkProactiveInstall();
      }, 3000); // 3 segundos después de cargar
    }
  }

  private checkProactiveInstall(): void {
    // Si no hay prompt automático disponible, mostrar botón manual
    if (!this.installPromptEvent && this.canInstallManually()) {
      this.installAvailableSubject.next(true);
    }
  }

  private canInstallManually(): boolean {
    // Verificar si es un dispositivo móvil o PWA compatible
    const userAgent = navigator.userAgent.toLowerCase();
    const isPWACompatible =
      /android/.test(userAgent) ||
      /iphone|ipad|ipod/.test(userAgent) ||
      'serviceWorker' in navigator;

    return isPWACompatible && !this.isAppInstalled && !this.manualPromptShown;
  }

  // Mostrar el prompt de instalación
  async showInstallPrompt(): Promise<boolean> {
    // Si hay prompt automático, usarlo
    if (this.installPromptEvent) {
      try {
        await this.installPromptEvent.prompt();
        const result = await this.installPromptEvent.userChoice;

        if (result.outcome === 'accepted') {
          this.isAppInstalled = true;
          this.saveInstallState('installed');
          this.installAvailableSubject.next(false);
          return true;
        } else {
          this.saveInstallState('dismissed');
          this.installReminderDismissed = true;
          return false;
        }
      } catch (error) {
        console.error('Error mostrando prompt de instalación:', error);
        return false;
      }
    }

    // Si no hay prompt automático, mostrar instrucciones manuales
    this.showManualInstallInstructions();
    this.manualPromptShown = true;
    return false;
  }

  // Verificar si hay prompt automático disponible
  hasAutomaticPrompt(): boolean {
    return !!this.installPromptEvent;
  }

  // Disparar prompt automático
  async triggerAutomaticPrompt(): Promise<boolean> {
    if (!this.installPromptEvent) {
      return false;
    }

    try {
      await this.installPromptEvent.prompt();
      const result = await this.installPromptEvent.userChoice;

      if (result.outcome === 'accepted') {
        this.isAppInstalled = true;
        this.saveInstallState('installed');
        this.installAvailableSubject.next(false);
        return true;
      } else {
        this.saveInstallState('dismissed');
        this.installReminderDismissed = true;
        return false;
      }
    } catch (error) {
      console.error('Error mostrando prompt automático:', error);
      return false;
    }
  }

  // Programar recordatorio
  scheduleReminder(delayMs: number): void {
    this.installAvailableSubject.next(false);
    setTimeout(() => {
      this.manualPromptShown = false;
      if (!this.isAppInstalled && !this.installReminderDismissed) {
        this.initializeProactiveInstallCheck();
      }
    }, delayMs);
  }

  // Mostrar instrucciones de instalación manual (ya no se usa)
  showManualInstallInstructions(): void {
    // Método mantenido para compatibilidad, pero ya no se usa
    // El modal ahora maneja esto
  }

  // Rechazar recordatorio de instalación
  dismissInstallReminder(): void {
    this.installReminderDismissed = true;
    this.installAvailableSubject.next(false);
    this.saveInstallState('dismissed');
  }

  // Activar actualización
  async activateUpdate(): Promise<boolean> {
    if (!this.swUpdate.isEnabled) {
      return false;
    }

    try {
      await this.swUpdate.activateUpdate();
      this.updateAvailableSubject.next(false);
      // Recargar la página para aplicar la actualización
      window.location.reload();
      return true;
    } catch (error) {
      console.error('Error activando actualización:', error);
      return false;
    }
  }

  // Verificar si la instalación está disponible
  isInstallAvailable(): boolean {
    return (!!this.installPromptEvent || this.canInstallManually()) && !this.isAppInstalled && !this.installReminderDismissed;
  }

  // Verificar si la app está instalada
  isInstalled(): boolean {
    return this.isAppInstalled;
  }

  // Verificar si se está ejecutando desde el navegador cuando está instalada
  isRunningInBrowser(): boolean {
    return this.isAppInstalled && 
           !window.matchMedia('(display-mode: standalone)').matches && 
           !(window.navigator as any).standalone;
  }

  // Abrir la app instalada (si está disponible)
  openInstalledApp(): void {
    if (this.isRunningInBrowser()) {
      // Intentar abrir la app mediante diferentes métodos
      const currentUrl = window.location.href;
      
      // Método 1: Intent URL para Android
      if (/android/i.test(navigator.userAgent)) {
        const intentUrl = `intent://${window.location.host}${window.location.pathname}#Intent;scheme=https;package=com.android.chrome;end`;
        window.location.href = intentUrl;
        return;
      }
      
      // Método 2: URL scheme personalizada
      try {
        window.location.href = `web+barclik://${window.location.host}${window.location.pathname}`;
      } catch (error) {
        // Si falla, mostrar mensaje
        alert('Para una mejor experiencia, abre BarClik desde el icono instalado en tu dispositivo');
      }
    }
  }

  // Obtener información de plataforma para instrucciones de instalación
  getPlatformInstructions(): { platform: string; instructions: string[] } {
    const userAgent = navigator.userAgent.toLowerCase();
    
    if (/iphone|ipad|ipod/.test(userAgent)) {
      return {
        platform: 'iOS',
        instructions: [
          '1. Toca el botón de compartir 📤 en Safari',
          '2. Desplázate y selecciona "Añadir a pantalla de inicio"',
          '3. Toca "Añadir" en la esquina superior derecha',
          '4. ¡Ya tienes BarClik en tu pantalla de inicio!'
        ]
      };
    } else if (/android/.test(userAgent)) {
      return {
        platform: 'Android',
        instructions: [
          '1. Toca el menú de opciones ⋮ en Chrome',
          '2. Selecciona "Instalar aplicación" o "Añadir a pantalla de inicio"',
          '3. Toca "Instalar" en el diálogo que aparece',
          '4. ¡Ya tienes BarClik instalada como una app!'
        ]
      };
    } else {
      return {
        platform: 'Desktop',
        instructions: [
          '1. Busca el icono de instalación + en la barra de direcciones',
          '2. Haz clic en "Instalar BarClik"',
          '3. Confirma la instalación en el diálogo',
          '4. ¡Ya tienes BarClik como aplicación de escritorio!'
        ]
      };
    }
  }

  // Resetear estado (para testing)
  resetInstallState(): void {
    localStorage.removeItem('pwa-install-state');
    localStorage.removeItem('pwa-install-reminder');
    this.installReminderDismissed = false;
    this.isAppInstalled = false;
  }

  private saveInstallState(state: 'installed' | 'dismissed'): void {
    localStorage.setItem('pwa-install-state', state);
    if (state === 'dismissed') {
      localStorage.setItem('pwa-install-reminder', 'dismissed');
    }
  }
}